import { ChromeReadyMarker } from "@-/dev.improve.haniverse.components.ui.chrome-ready-marker";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { LearnSummaryPageLoadingFallback } from "#learn-summary-page-loading-fallback";
import { LearnSummaryPageSuspense } from "#learn-summary-page-suspense";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { LearnSummaryHeaderContext } from "#learn-summary-header-context";
import { useLearnSummaryIsCurrent, useLearnSummaryIsUnderMap } from "#learn-summary-presence";
import { useEffect, useState, type ReactNode } from "react";
import { Head } from "@-/com.npmjs.expo-router/head";
import { useNavToolbarPublisher } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { LearnDeckHeaderControls } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.header";

/**
 * The summary keeps the deck's toolbar cluster. Its map button is hidden while the Welcome Back
 * card holds one of its own beside Start, and opens the map from any other summary. The
 * summary has no review history of its own, so the default (empty) `LearnDeckHistoryContext`
 * leaves Undo and Redo disabled. The deck publishes its own live cluster once it takes focus. The
 * cluster stays published while Learn's map is open over the summary, so it leaves and returns
 * with the toolbar rather than vanishing.
 */
function useLearnSummaryToolbar(isCurrent: boolean) {
  const publishToolbar = useNavToolbarPublisher();
  useEffect(() => {
    if (publishToolbar === null || !isCurrent) {
      return;
    }
    return publishToolbar({
      actions: null,
      leading: null,
      panels: <LearnDeckHeaderControls isTransitioning={false} savedActionUndo={null} />,
    });
  }, [isCurrent, publishToolbar]);
}

/**
 * The summary itself: the Learn header, and the suspense-wrapped welcome card with its controls.
 * `LearnSummaryHost` renders it above the deck; the chrome it publishes (title, toolbar, startup
 * marker) belongs to the summary only while it is the current screen, so a departing summary
 * leaves the chrome to the deck that replaced it.
 */
export function LearnSummarySurface() {
  const [headerTitle = "Review", setHeaderTitle] = useState<ReactNode>(null);
  const isCurrent = useLearnSummaryIsCurrent();
  const isUnderMap = useLearnSummaryIsUnderMap();
  useLearnSummaryToolbar(isCurrent || isUnderMap);
  return (
    <LearnSummaryHeaderContext value={setHeaderTitle}>
      {/* Both the local fallback and resolved view include the card and bottom controls.
          Release startup here, after the Learn toolbar and this surface commit together. */}
      {isCurrent ? <ChromeReadyMarker /> : null}
      {isCurrent ? (
        <Head>
          <title>Review — Han</title>
        </Head>
      ) : null}
      {isCurrent ? (
        <PrimaryControlsStripTitle>{headerTitle ?? "Review"}</PrimaryControlsStripTitle>
      ) : null}
      {/* A page-shaped fallback, NOT the default spinner: this screen is entered cold (bottom-tab
          `jumpTo` and the cold-start index `Redirect` both skip `prepare()`), so the fallback is what
          a returning user actually sees on launch. It renders the real frame and greeting. */}
      <SuspensePageBoundary
        fallback={<LearnSummaryPageLoadingFallback />}
        location={import.meta.location}
      >
        <LearnSummaryPageSuspense />
      </SuspensePageBoundary>
    </LearnSummaryHeaderContext>
  );
}
